import { Suspense, lazy } from 'react';
import type { ApiClient } from '../api';
import { BRAND_META } from '../brand';
import { AppDownloads } from '../components/AppDownloads';
import { Feed } from '../components/Feed';
import { EmptyPanel } from '../components/EmptyPanel';
import { Sparkline } from '../components/Sparkline';
import { TopTable, deeplinkEntityFor } from '../components/TopTable';
import { compact, isZeroMoney, moneyTotals } from '../format';
import { bucketise, usePoll } from '../hooks';
import type {
  BrandName,
  BrandTiles,
  FeedEvent,
  GeoResponse,
  Me,
  SeriesPoint,
  Summary,
  TopRow,
} from '../types';

const GeoMap = lazy(() => import('../components/GeoMap').then((m) => ({ default: m.GeoMap })));

/**
 * One brand's view: the §2.1.6 per-brand tiles with 7-day trends, then
 * that brand's map, feed and leaderboards. An uninstrumented brand shows
 * what is coming rather than zeros pretending to be data.
 */
export function BrandView({
  api,
  me,
  brand,
  mapStyleUrl,
  onDeepLink,
}: {
  api: ApiClient;
  me: Me;
  brand: BrandName;
  mapStyleUrl: string;
  onDeepLink: (brand: BrandName, entity: string, id: string | null) => void;
}) {
  const summary = usePoll<Summary>(
    () => api.get(`/api/dashboard/summary?brand=${brand}`),
    30_000,
    ['summary', brand],
  );
  const series = usePoll<{ points: SeriesPoint[] }>(
    () => api.get(`/api/dashboard/series?brand=${brand}&hours=168`),
    120_000,
    ['series', brand],
  );
  const top = usePoll<{ rows: TopRow[] }>(
    () => api.get(`/api/dashboard/top?brand=${brand}`),
    120_000,
    ['top', brand],
  );
  const geo = usePoll<GeoResponse>(() => api.get(`/api/geo?brand=${brand}&hours=168`), 120_000, ['geo', brand]);
  const feed = usePoll<{ events: FeedEvent[] }>(
    () => api.get(`/api/feed?brand=${brand}&limit=40`),
    15_000,
    ['feed', brand],
  );

  if (summary.error) {
    return <div className="load-error">The read model is unreachable: {summary.error.message}</div>;
  }
  const today = summary.data?.today[brand];
  const mtd = summary.data?.mtd[brand];
  const meta = BRAND_META[brand];

  if (today && !today.instrumented) {
    return (
      <div className="grid">
        <div className="card pending-card">
          <h3>{meta.label} is not instrumented yet</h3>
          <p>
            EqualiShop event instrumentation is phase one milestone 0, in Nick's hands, and it is the
            critical path for this view. Nothing is broken here; there is simply no event stream yet.
          </p>
          <p>
            When events flow, this page fills itself: redemptions, member growth, partner engagement,
            the activity map and the live feed. No dashboard change will be needed.
          </p>
        </div>
      </div>
    );
  }

  // `subtract` takes revenue back out: a clawback lowers the line on the
  // day it lands, as it lowers the tile.
  const trend = (types: string[], useRevenue = false, subtract: string[] = []): number[] =>
    bucketise(
      (series.data?.points ?? [])
        .filter((p) => p.brand === brand && (types.includes(p.event_type) || subtract.includes(p.event_type)))
        .map((p) => ({
          bucket_start: p.bucket_start,
          value: useRevenue
            ? (subtract.includes(p.event_type) ? -1 : 1) * (p.revenue_minor_sum ?? 0)
            : p.event_count,
        })),
      28,
      168,
    );

  const revenueRedacted = today?.revenue === null;

  const tiles: {
    key: string;
    label: string;
    value: string;
    sub?: string;
    spark?: number[];
    redacted?: boolean;
    /** A true zero, as distinct from redacted: it renders dimmed with the
     *  brand's stated reason, because an unexplained zero reads as broken
     *  rather than early. */
    empty?: boolean;
  }[] = today
    ? [
        {
          key: 'revenue',
          label: 'Revenue today',
          value: moneyTotals(today.revenue),
          sub: mtd
            ? `${moneyTotals(mtd.revenue)} month to date${(() => {
                const goal = summary.data?.goals?.[brand];
                const current = goal?.monthlyRevenueMinor && mtd.revenue ? (mtd.revenue[goal.revenueCurrency] ?? 0) : null;
                return goal?.monthlyRevenueMinor && current !== null
                  ? ` · ${Math.round(Math.min(100, (current / goal.monthlyRevenueMinor) * 100))}% of the monthly goal`
                  : '';
              })()}`
            : undefined,
          spark: revenueRedacted ? undefined : trend(
                ['transaction.recorded', 'transaction.confirmed', 'conversion.approved'],
                true,
                ['transaction.reversed', 'conversion.reversed'],
              ),
          redacted: revenueRedacted,
          empty: isZeroMoney(today.revenue),
        },
        {
          key: 'tx',
          label: `${meta.txLabel} today`,
          value: compact(today.transactionCount),
          sub: today.transactionValue ? `${moneyTotals(today.transactionValue)} gross` : undefined,
          spark: trend(['transaction.recorded', 'conversion.recorded']),
          empty: today.transactionCount === 0,
        },
        // Cashblack clicks out to merchants count from 28 September 2026:
        // the click is what places a purchase on the atlas.
        ...(brand === 'afrofiliate' || brand === 'cashblack'
          ? [
              {
                key: 'clicks',
                label: 'Clicks today',
                value: compact(today.clicks),
                spark: trend(['click.recorded']),
                empty: today.clicks === 0,
              },
            ]
          : []),
        {
          key: 'users',
          label: `${meta.usersLabel} today`,
          value: compact(today.newUsers),
          spark: trend(['member.joined', 'creator.joined']),
          empty: today.newUsers === 0,
        },
        { key: 'partners', label: `${meta.partnersLabel} today`, value: compact(today.newPartners), empty: today.newPartners === 0 },
        {
          key: 'payouts',
          label: 'Payouts today',
          value: moneyTotals(today.payoutValue),
          sub: `${compact(today.payoutCount)} payments`,
          redacted: today.payoutValue === null,
          empty: isZeroMoney(today.payoutValue) && today.payoutCount === 0,
        },
      ]
    : [];

  return (
    <>
      {/* Per-brand assessment. Matthew chose a genuine per-brand analysis
        * over the group brief scoped to a brand (4 September 2026), and
        * that analysis does not exist yet, so this states what it is
        * waiting for rather than reskinning the group brief as one. A
        * brand assessment that is really the group summary would be the
        * kind of confident wrongness the empty states exist to avoid. */}
      <EmptyPanel
        title={`${meta.label} assessment`}
        reason="No per-brand assessment yet. The group overnight brief is not it, and showing that here would misrepresent a group summary as brand analysis."
      />

      <div className="grid tiles">
        {tiles.map((t) => (
          <div key={t.key} className="card tile">
            <div className="label">{t.label}</div>
            {t.redacted ? (
              <div className="redacted">hidden for your role</div>
            ) : (
              <div className={t.empty ? 'value value-empty' : 'value'}>{t.value}</div>
            )}
            {t.empty && !t.redacted ? (
              <div className="tile-note">{meta.emptyReason}</div>
            ) : (
              t.sub && !t.redacted && <div className="sub">{t.sub}</div>
            )}
            {t.spark && t.spark.some((v) => v > 0) && <Sparkline points={t.spark} color={meta.series} />}
          </div>
        ))}
      </div>

      {/* App downloads (24 October 2026): this brand's apps, when it has any. */}
      <AppDownloads api={api} brand={brand} />

      <div className="grid map-row">
        <div className="card map-card">
          <div className="map-overlay-head">
            <span className="card-title">{meta.label} activity, last 7 days</span>
          </div>
          <Suspense fallback={<div className="map-container" />}>
            <GeoMap
              api={api}
              cells={geo.data?.cells ?? []}
              styleUrl={mapStyleUrl}
              suppressed={geo.data?.suppressed}
              subThreshold={geo.data?.subThreshold ?? []}
              marks={geo.data?.marks ?? []}
              threshold={geo.data?.suppressionThreshold ?? 5}
              hours={geo.data?.hours ?? 24}
            />
          </Suspense>
          <span className="map-note">
            A place shows once it has {geo.data?.suppressionThreshold ?? 5} of the same kind of event; fewer sit at
            the country's centre, never a fine location
            {geo.data && geo.data.suppressed.events > 0
              ? ` · ${geo.data.suppressed.events} ${geo.data.suppressed.events === 1 ? 'event' : 'events'} currently below it`
              : ''}
          </span>
          <span className="map-attrib">
            © <a href="https://openfreemap.org" target="_blank" rel="noreferrer">OpenFreeMap</a> ·{' '}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OSM</a>
          </span>
        </div>
        <div className="card">
          <div className="card-head">
            <span className="card-title">
              <span className="live-pip" aria-hidden />
              Live events
            </span>
          </div>
          <Feed
            api={api}
            events={feed.data?.events ?? null}
            onDeepLink={(e: FeedEvent) => {
              const [entity, id] = e.offer_id
                ? ['offer', e.offer_id]
                : e.creator_id
                  ? ['creator', e.creator_id]
                  : e.merchant_id
                    ? ['merchant', e.merchant_id]
                    : ['advertiser', e.advertiser_id];
              onDeepLink(brand, entity!, id ?? null);
            }}
          />
        </div>
      </div>

      <div className="grid cards-2">
        {['offer', 'creator', 'merchant', 'advertiser'].map((entityType) => (
          <TopTable
            key={entityType}
            rows={top.data?.rows ?? []}
            entityType={entityType}
            me={me}
            onDeepLink={(row) => {
              const entity = deeplinkEntityFor(row.entity_type);
              if (entity) onDeepLink(row.brand, entity, row.entity_id);
            }}
          />
        ))}
      </div>
    </>
  );
}
